import { FC, useEffect } from 'react'
import { Alert, Box, Button, CircularProgress, Typography } from '@mui/material'
import { VerifiedUserOutlined } from '@mui/icons-material'
import { useNavigate } from 'react-router-dom'
import { useIntl } from 'react-intl'
import { clarity } from 'react-microsoft-clarity'
import InfoIndicator from 'components/InfoIndicator'
import { useInsuranceConsentsList } from 'services/insurance/insurance.hooks'
import InsuranceConsentItem from './InsuranceConsentItem'

const InsuranceConsent: FC = () => {
  const { formatMessage } = useIntl()
  const navigate = useNavigate()
  const { consentsList, isLoading, isFetching, isError, refetchConsentsList } =
    useInsuranceConsentsList()

  useEffect(() => {
    if (clarity.hasStarted()) {
      clarity.setEvent('insurance_consent_viewed')
    }
  }, [])

  return (
    <Box sx={{ maxWidth: '800px', mx: 'auto', width: '100%', px: { xs: 2, md: 0 }, pb: 4 }}>
      <Typography variant='h6' component='h1' sx={{ my: 2, display: { md: 'none' } }}>
        {formatMessage({
          id: 'insuranceConsent.title',
          defaultMessage: 'Potwierdzenie zgody na ubezpieczenie'
        })}
      </Typography>

      {isLoading && (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 2,
            my: 6
          }}>
          <CircularProgress />
          <Typography variant='body2' color='text.secondary'>
            {formatMessage({
              id: 'insuranceConsent.loading',
              defaultMessage: 'Ładowanie danych zgody...'
            })}
          </Typography>
        </Box>
      )}

      {isError && !isLoading && (
        <Alert
          severity='error'
          sx={{ my: 2 }}
          action={
            <Button color='inherit' size='small' onClick={() => refetchConsentsList()}>
              {formatMessage({
                id: 'insuranceConsent.retry',
                defaultMessage: 'Spróbuj ponownie'
              })}
            </Button>
          }>
          {formatMessage({
            id: 'insuranceConsent.error',
            defaultMessage: 'Wystąpił błąd. Spróbuj ponownie później'
          })}{' '}
          <Button
            color='inherit'
            size='small'
            sx={{ textDecoration: 'underline', p: 0, minWidth: 'unset', verticalAlign: 'baseline' }}
            onClick={() => navigate('/help')}>
            {formatMessage({
              id: 'insuranceConsent.support',
              defaultMessage: 'Skontaktuj się z pomocą'
            })}
          </Button>
        </Alert>
      )}

      {!isLoading && !isError && (
        <>
          <InfoIndicator
            helperText={formatMessage({
              id: 'insuranceConsent.empty',
              defaultMessage: 'Brak zgód do potwierdzenia'
            })}
            Icon={VerifiedUserOutlined}
            isVisible={!consentsList.length && !isFetching}
          />
          {consentsList.map((consent) => (
            <InsuranceConsentItem key={consent.id} consent={consent} />
          ))}
        </>
      )}
    </Box>
  )
}

export default InsuranceConsent
